import { ColDef } from "ag-grid-community";

// ── DataTable primitive ──────────────────────────────────────────────────
// Single-component `<DataTable>` — paginated, sortable, selectable AG Grid

// wrapper. Pair with `<ResourceView.Root>` for filter UI.
export { DataTable, type DataTableProps } from "./DataTable";

/**
 * A `defaultColDef` that makes a grid's columns flex to fill the available
 * width. Pass to `<DataTable defaultColDef={FLEX_FILL_COL_DEF}>` on tables that
 * should fill their container (the feature-page resource tables do); columns
 * that want a larger share raise their own `flex`, and control columns opt out
 * with `flex: 0`. The primitive's built-in default is intentionally sizing-
 * neutral, so tables that don't pass this keep normal `width` behavior. Stable
 * reference so AG Grid doesn't re-define columns each render.
 */
export const FLEX_FILL_COL_DEF: ColDef = { flex: 1 };

// ── Table state hook ────────────────────────────────────────────────────
// `useTable` owns truly table-only state (sort, pagination, selection,
// column visibility). For filter state, use `useResourceView` from
// `@/shared/components/ResourceView`.
export {
  DEFAULT_PAGE_SIZE_OPTIONS,
  deriveLoadingState,
  functionalUpdate,
  useTable,
  type DataTableHandle,
  type DataTableLoadingState,
  type DataTablePreferences,
  type DataTableSort,
  type DataTableSortDirection,
  type Table,
  type Updater,
  type UseTableOptions,
} from "./useTable";

// ── Column helpers ──────────────────────────────────────────────────────
export { createColumnHelper } from "./createColumnHelper";
export type { ColumnPickerOption } from "./columnVisibility";
export {
  buildColumnsForPreferences,
  colId,
  type BuildColumnsForPreferencesParams,
} from "./buildColumnsForPreferences";

// "Columns" button + visibility picker popover. Table-coupled chrome —
// stays at the primitive layer (not part of the `ResourceView` compound)
// because it only makes sense when there's a table with columns to toggle.
export { ColumnVisibilityPicker as ColumnVisibility } from "./ColumnVisibilityPicker";

// ── Shared table styles ─────────────────────────────────────────────────
export { default as cellStyles } from "./cellStyles.module.css";

// ── Shared cell values ──────────────────────────────────────────────────
// Em-dash placeholder + formatter for empty cells, shared so every list
// renders absent values identically.
export { renderDashOr, renderDashOrValue } from "./cellValues";

// ── Cell components ─────────────────────────────────────────────────────
export { EmptyCell } from "./Cells/EmptyCell";
export {
  TimestampCell,
  timestampCellClass,
  type TimestampVariant,
} from "./Cells/TimestampCell";
export { useRelativeTick } from "./Cells/TimestampCell/useRelativeTick";

// AG Grid `ColDef` is part of the DataTable public contract. Consumers configure
// columns with AG Grid types, including cell renderer / value getter / formatter
// params. Re-exported here so callers depend on the primitive barrel rather than
// reaching into `ag-grid-community` directly.
export type {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
